import { User, UserManager } from 'oidc-client';
import appUserManager from './user-manager';
import {
    ORGANIZATIONS_CLAIM,
    parseOrganizationsClaim,
} from '../organizations/organizations-claim';
import requestInstance from '../../utils/instance-utils';
import Routes from '../../constants/routes';

export const CHANGE_ORGANIZATION_PROMPT = 'select_organization';

interface IAuthUser {
    id_token?: string | undefined;
    access_token?: string | undefined;
    expires_at?: number;
    profile?: any;
    state?: any;
}

// Reads the tenant (ECR id) from a JWT payload. Used only to point the URL at
// the selected organization; the core verifies the token itself.
export function getTenantFromToken(token?: string): string | undefined {
    try {
        const payload = token?.split('.')[1];
        if (!payload) {
            return undefined;
        }
        const json = decodeURIComponent(
            atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
                .split('')
                .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join('')
        );
        const { tenant } = JSON.parse(json);
        return typeof tenant === 'string' && tenant ? tenant : undefined;
    } catch (error) {
        return undefined;
    }
}

export function authenticateUser(
    identityAccessToken: string,
    identityAccessTokenExpiration: number,
    accessToken?: string
) {
    return requestInstance.get('/login', {
        headers: {
            authorization: `Bearer ${identityAccessToken}`,
            expiration: identityAccessTokenExpiration,
            ...(accessToken && { 'x-access-token': accessToken }),
        },
        params: {
            pathParamEcrId: sessionStorage.getItem('ecrId'),
        },
    });
}

export async function initiateLogin() {
    try {
        const fetchUserManager = await appUserManager();
        await fetchUserManager.signinRedirect({
            state: {
                originalRedirectUrl: `${window.location.pathname}${window.location.hash}`,
            },
        });
    } catch (error: any) {
        handleError('Auth: initiate login error', error);
    }
}

// Separate from initiateLogin so the standard sign-in request never carries
// the prompt. Reuses the same user manager, so client_id / redirect_uri /
// response_type / scope / state / nonce are identical to a normal login.
export async function initiateChangeOrganization() {
    try {
        const fetchUserManager = await appUserManager();
        await fetchUserManager.signinRedirect({
            prompt: CHANGE_ORGANIZATION_PROMPT,
            state: {
                originalRedirectUrl: `${window.location.pathname}${window.location.hash}`,
                changeOrganization: true,
            },
        });
    } catch (error: any) {
        handleError('Auth: initiate change organization error', error);
    }
}

export async function silentUserLogin() {
    try {
        const fetchUserManager = await appUserManager();
        await fetchUserManager.signinSilent();
    } catch (error: any) {
        console.log('Auth: silent login error');
    }
}

export async function initiateLogout() {
    try {
        const fetchUserManager = await appUserManager();
        // sessionStorage outlives sign-out in the same tab; a stale ECR id
        // would be sent to /login by the next session and rejected.
        sessionStorage.removeItem('ecrId');
        await fetchUserManager.signoutRedirect();
    } catch (error: any) {
        handleError('Auth: initiate logout error', error, true);
    }
}

// After an organization switch, or a sign-in that picked a different org than
// the URL the user started from, the token belongs to another ECR id than the
// URL (and sessionStorage) carry, so /login would be rejected with an ECR id
// mismatch. Align both with the token's tenant and persist the corrected
// redirect so a later reload doesn't restore the old org.
async function applyOrganizationSwitch(
    fetchUserManager: UserManager,
    user: User
) {
    // NeoID carries the selected organization as the access token's tenant.
    const tenant =
        getTenantFromToken(user?.access_token) || user?.profile?.tenant;
    const storedEcrId = sessionStorage.getItem('ecrId');
    const isDifferentOrganization = storedEcrId !== tenant;
    if (
        !tenant ||
        !(user?.state?.changeOrganization || isDifferentOrganization)
    ) {
        return;
    }
    sessionStorage.setItem('ecrId', tenant);
    user.state = {
        ...user.state,
        originalRedirectUrl: Routes.HOME.replace(
            ':ecrId',
            encodeURIComponent(tenant)
        ),
    };
    await fetchUserManager.storeUser(user);
}

export async function processLoginCallback() {
    try {
        const fetchUserManager = await appUserManager();
        const userInfo = await fetchUserManager.signinRedirectCallback();
        await applyOrganizationSwitch(fetchUserManager, userInfo);
        return await getUserInfo(userInfo);
    } catch (error: any) {
        return handleError('Auth: process login callback error', error);
    }
}

export async function processSilentLoginCallback() {
    try {
        const fetchUserManager = await appUserManager();
        fetchUserManager.signinSilentCallback();
    } catch (error: any) {
        return handleError('signinSilentCallback error', error);
    }
}

export async function getUserInfo(userInfo?: User | null) {
    let oAuthUser = userInfo;
    try {
        const fetchUserManager = await appUserManager();
        if (!userInfo) {
            oAuthUser = await fetchUserManager.getUser();
        }
        const {
            id_token: identityAccessToken,
            access_token: accessToken,
            expires_at: identityAccessTokenExpiration,
            profile,
            state: { originalRedirectUrl },
        }: IAuthUser = { ...oAuthUser };
        const { email, given_name: firstName, family_name: lastName } = profile;
        return {
            identityAccessToken,
            identityAccessTokenExpiration,
            accessToken,
            isUserAuthenticated: true,
            role: 'admin',
            email,
            firstName,
            lastName,
            originalRedirectUrl,
            ...getOrganizations(profile),
        };
    } catch (error: any) {
        return handleError('Auth: set access token error', error);
    }
}

export async function getAndCheckUserInfo(userInfo?: User | null) {
    let oAuthUser = userInfo;
    try {
        const fetchUserManager = await appUserManager();
        if (!userInfo) {
            oAuthUser = await fetchUserManager.getUser();
            if (
                !oAuthUser &&
                localStorage.getItem('IS_USER_AUTHENTICATED') === 'true'
            ) {
                await fetchUserManager.signinRedirect({
                    state: {
                        originalRedirectUrl: `${window.location.pathname}${window.location.hash}`,
                    },
                });
            }
        }
        const {
            id_token: identityAccessToken,
            access_token: accessToken,
            expires_at: identityAccessTokenExpiration,
            profile,
        }: IAuthUser = { ...oAuthUser };
        const { email, given_name: firstName, family_name: lastName } = profile;
        return {
            identityAccessToken,
            identityAccessTokenExpiration,
            accessToken,
            isUserAuthenticated: true,
            role: 'admin',
            email,
            firstName,
            lastName,
            originalRedirectUrl: oAuthUser?.state?.originalRedirectUrl,
            ...getOrganizations(profile),
        };
    } catch (error: any) {
        return handleError('Auth: set access token error', error);
    }
}

function getOrganizations(profile: any) {
    const claim = profile?.[ORGANIZATIONS_CLAIM];
    const { organizations, isMalformed } = parseOrganizationsClaim(claim);
    if (isMalformed) {
        console.error(`Auth: malformed ${ORGANIZATIONS_CLAIM} claim`, claim);
    }
    return { organizations };
}

function handleError(errMsg: string, err: any, isUserAuthenticated?: boolean) {
    const data = {
        isUserAuthenticated: isUserAuthenticated || false,
        role: 'admin',
        error: err,
        errorMessage: errMsg,
    };
    throw data;
}
